import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { PageLoader, SkeletonLoader, ButtonLoader } from '../components/ui/loader';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { 
  Search, 
  FileText, 
  MoreVertical, 
  Eye, 
  Trash2, 
  Filter,
  Upload,
  Image,
  FileSpreadsheet,
  Presentation,
  Play,
  Network,
  MessageCircle
} from 'lucide-react';
import { toast } from 'sonner';
import DocumentGraph from '../components/DocumentGraph';
import DocumentChat from '../components/DocumentChat';

const IMAGE_EXTENSIONS = ['.png', '.jpg', '.jpeg', '.gif', '.bmp', '.tiff', '.tif', '.webp', '.heic'];
const SPREADSHEET_EXTENSIONS = ['.xls', '.xlsx', '.csv'];
const PRESENTATION_EXTENSIONS = ['.ppt', '.pptx'];

const getFileIcon = (fileType) => {
  if (!fileType) return FileText;
  if (IMAGE_EXTENSIONS.includes(fileType)) return Image;
  if (SPREADSHEET_EXTENSIONS.includes(fileType)) return FileSpreadsheet;
  if (PRESENTATION_EXTENSIONS.includes(fileType)) return Presentation;
  return FileText;
};

export default function Documents() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [languageFilter, setLanguageFilter] = useState('all');
  const [selectedDocs, setSelectedDocs] = useState([]);
  const [batchValidating, setBatchValidating] = useState(false);
  const [showGraph, setShowGraph] = useState(false);
  const [graphData, setGraphData] = useState({ nodes: [], edges: [] });
  const [loadingGraph, setLoadingGraph] = useState(false);
  const [globalChatOpen, setGlobalChatOpen] = useState(false);

  useEffect(() => {
    fetchDocuments();
  }, [statusFilter, languageFilter]);

  const fetchDocuments = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter && statusFilter !== 'all') params.append('status', statusFilter);
      if (languageFilter && languageFilter !== 'all') params.append('language', languageFilter);
      if (search) params.append('search', search);

      const response = await api().get(`/documents?${params.toString()}`);
      setDocuments(response.data);
    } catch (error) {
      toast.error('Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  const fetchRelationshipGraph = async () => {
    setLoadingGraph(true);
    try {
      const response = await api().get('/documents/relationship-graph');
      setGraphData(response.data);
      setShowGraph(true);
    } catch (error) {
      toast.error('Failed to load relationship graph');
    } finally {
      setLoadingGraph(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchDocuments();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;
    
    try {
      await api().delete(`/documents/${id}`);
      toast.success('Document deleted');
      fetchDocuments();
    } catch (error) {
      toast.error('Failed to delete document');
    }
  };

  const handleSelectDoc = (docId, checked) => {
    if (checked) {
      setSelectedDocs(prev => [...prev, docId]);
    } else {
      setSelectedDocs(prev => prev.filter(id => id !== docId));
    }
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedDocs(documents.map(d => d.id));
    } else {
      setSelectedDocs([]);
    }
  };

  const handleBatchValidate = async () => {
    if (selectedDocs.length === 0) {
      toast.error('Please select at least one document');
      return;
    }

    setBatchValidating(true);
    try {
      const response = await api().post('/documents/batch-validate', {
        document_ids: selectedDocs
      });
      toast.success(`Batch validation complete: ${response.data.successful}/${response.data.total_documents} successful`);
      navigate(`/batch-reports/${response.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Batch validation failed');
    } finally {
      setBatchValidating(false);
    }
  };

  const handleNodeClick = (node) => {
    navigate(`/documents/${node.id}`);
  };

  const getStatusBadge = (status) => {
    const variants = {
      uploaded: { className: 'bg-gray-100 text-gray-700', label: 'Uploaded' },
      parsed: { className: 'bg-blue-100 text-blue-700', label: 'Parsed' },
      validated: { className: 'bg-emerald-100 text-emerald-700', label: 'Validated' },
      issues_found: { className: 'bg-red-100 text-red-700', label: 'Issues Found' },
    };
    const config = variants[status] || { className: 'bg-gray-100', label: status };
    return <Badge className={config.className}>{config.label}</Badge>;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="documents-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold font-['Manrope']">Documents</h1>
          <p className="text-muted-foreground">Manage and view all uploaded documents</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setGlobalChatOpen(true)}>
            <MessageCircle className="h-4 w-4 mr-2" />
            Chat with All Docs
          </Button>
          <Button variant="outline" onClick={fetchRelationshipGraph} disabled={loadingGraph}>
            {loadingGraph ? <ButtonLoader /> : <Network className="h-4 w-4 mr-2" />}
            Relationship Graph
          </Button>
          <Link to="/upload">
            <Button data-testid="upload-btn">
              <Upload className="h-4 w-4 mr-2" />
              Upload
            </Button>
          </Link>
        </div>
      </div>

      {/* Global Chat Dialog */}
      <DocumentChat
        isGlobal={true}
        open={globalChatOpen}
        onOpenChange={setGlobalChatOpen}
      />

      {/* Relationship Graph Modal */}
      {showGraph && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-['Manrope']">Documents Relationship Graph</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setShowGraph(false)}>
                Close
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {graphData.nodes.length > 0 ? (
              <div className="border rounded-lg overflow-hidden">
                <DocumentGraph 
                  nodes={graphData.nodes}
                  edges={graphData.edges}
                  onNodeClick={handleNodeClick}
                  width={1000}
                  height={500}
                />
                <div className="p-4 bg-muted/30 border-t">
                  <div className="flex items-center gap-6 text-sm">
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded-full bg-emerald-500"></div>
                      <span>Validated</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded-full bg-red-500"></div>
                      <span>Issues Found</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded-full bg-gray-500"></div>
                      <span>Other</span>
                    </div>
                    <span className="text-muted-foreground ml-4">Click on a node to view document</span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-12">
                <Network className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                <p className="text-muted-foreground">No relationships found</p>
                <p className="text-sm text-muted-foreground mt-1">Extract references from documents to see relationships</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <form onSubmit={handleSearch} className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search documents..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10"
                  data-testid="search-input"
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-muted-foreground" />
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[140px]" data-testid="status-filter">
                  <SelectValue placeholder="All Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="uploaded">Uploaded</SelectItem>
                  <SelectItem value="parsed">Parsed</SelectItem>
                  <SelectItem value="validated">Validated</SelectItem>
                  <SelectItem value="issues_found">Issues Found</SelectItem>
                </SelectContent>
              </Select>
              <Select value={languageFilter} onValueChange={setLanguageFilter}>
                <SelectTrigger className="w-[150px]" data-testid="language-filter">
                  <SelectValue placeholder="All Languages" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Languages</SelectItem>
                  <SelectItem value="en">English</SelectItem>
                  <SelectItem value="ar">Arabic</SelectItem>
                  <SelectItem value="es">Spanish</SelectItem>
                  <SelectItem value="fr">French</SelectItem>
                  <SelectItem value="de">German</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button type="submit" data-testid="search-btn">Search</Button>
          </form>
        </CardContent>
      </Card>

      {/* Batch Actions */}
      {selectedDocs.length > 0 && (
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-blue-700">
                {selectedDocs.length} document(s) selected
              </span>
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setSelectedDocs([])}
                >
                  Clear Selection
                </Button>
                <Button 
                  size="sm"
                  onClick={handleBatchValidate}
                  disabled={batchValidating}
                  data-testid="batch-validate-btn"
                >
                  {batchValidating ? <ButtonLoader /> : <Play className="h-4 w-4 mr-2" />}
                  Run Batch Validation
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Documents Table */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg font-['Manrope']">
            All Documents ({documents.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-2">
              {[...Array(5)].map((_, i) => (
                <SkeletonLoader key={i} variant="table-row" />
              ))}
            </div>
          ) : documents.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[50px]">
                    <Checkbox 
                      checked={selectedDocs.length === documents.length && documents.length > 0}
                      onCheckedChange={handleSelectAll}
                      data-testid="select-all-checkbox"
                    />
                  </TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Title</TableHead>
                  <TableHead>Language</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="w-[50px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {documents.map((doc) => {
                  const FileIcon = getFileIcon(doc.file_type);
                  return (
                    <TableRow key={doc.id} data-testid={`doc-row-${doc.id}`}>
                      <TableCell>
                        <Checkbox 
                          checked={selectedDocs.includes(doc.id)}
                          onCheckedChange={(checked) => handleSelectDoc(doc.id, checked)}
                          data-testid={`select-doc-${doc.id}`}
                        />
                      </TableCell>
                      <TableCell>
                        <Link 
                          to={`/documents/${doc.id}`}
                          className="flex items-center gap-2 hover:text-blue-600 transition-colors"
                        >
                          <FileIcon className="h-4 w-4 text-muted-foreground" />
                          <span className="font-medium">{doc.name}</span>
                        </Link>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {doc.title || '-'}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="uppercase">
                          {doc.language}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {getStatusBadge(doc.status)}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {formatDate(doc.created_at)}
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" data-testid={`doc-menu-${doc.id}`}>
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem asChild>
                              <Link to={`/documents/${doc.id}`}>
                                <Eye className="h-4 w-4 mr-2" />
                                View
                              </Link>
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              className="text-red-600"
                              onClick={() => handleDelete(doc.id)}
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-12">
              <FileText className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
              <p className="text-muted-foreground">No documents found</p>
              <Link to="/upload">
                <Button className="mt-4">
                  <Upload className="h-4 w-4 mr-2" />
                  Upload First Document
                </Button>
              </Link>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
